// Shared strategy-performance stat helpers for the Market Health cards.
//
// Two responsibilities live here so StrategyChart, DrawdownChart and the
// Strategy Performance (formerly Trade History) card can share them:
//
//   1. VALUE FORMATTERS (formatPct/formatRatio/…) — previously inlined in
//      StrategyChart. Hoisted verbatim so every card renders a stat the same
//      way (same sign convention, same '—' fallback for non-finite values).
//
//   2. computeWindowStats() — derives the same stat set the backend serves in
//      `strategy[mode]` (total return, CAGR, volatility, Sharpe, Martin Ratio,
//      max drawdown, time invested — for both the strategy and SPY buy-and-
//      hold), but over an ARBITRARY windowed slice of history rather than the
//      full backend history. This is what lets each card's stats react to its
//      own selected range/crash preset (the "picker"): the card passes the
//      same windowed point array it already builds for the chart, and gets
//      back stats scoped to exactly that window. The math mirrors
//      market-health-compute/index.mjs's computeSeriesStats / computeUlcerIndex
//      / computeMartinRatio / computeTimeInvestedPct so a window covering the
//      full history reproduces the backend's served numbers.

import { selectLag } from './strategyLagSelect';


const TRADING_DAYS_PER_YEAR = 252;

// ─── Formatters (hoisted from StrategyChart) ────────────────────────────────

// Number.isFinite (not typeof === 'number') so a NaN/Infinity that ever leaks
// through renders as the intended '—' fallback instead of literal
// "NaN%"/"Infinity%".
export const formatPct = (value) =>
  (Number.isFinite(value) ? `${value >= 0 ? '+' : ''}${value.toFixed(1)}%` : '—');

export const formatRatio = (value) => (Number.isFinite(value) ? value.toFixed(2) : '—');

// Time Invested (spec §2.3) is always non-negative — no leading '+'.
export const formatUnsignedPct = (value) =>
  (Number.isFinite(value) ? `${value.toFixed(1)}%` : '—');

// Drawdown delta, in percentage points (Drawdown card's Δ vs SPY row).
export const formatDeltaPp = (value) =>
  (Number.isFinite(value) ? `${value >= 0 ? '+' : ''}${value.toFixed(1)}pp` : '—');

// ─── Windowed stat math (mirrors market-health-compute/index.mjs) ───────────

const mean = (arr) => arr.reduce((sum, v) => sum + v, 0) / arr.length;
const round = (value, digits) => {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
};

// Core series stats over an ascending list of values (SPY price or strategy
// equity). Returns percentages (cagr/volatility/maxDrawdown) and a raw ratio
// (sharpe). `null` fields on an under-2-point window, matching the backend.
export const computeSeriesStats = (valuesAscending) => {
  const empty = {
    totalReturn: null, cagr: null, volatility: null, sharpe: null, maxDrawdown: null,
  };
  const totalDays = (valuesAscending || []).length;
  const valid = (valuesAscending || []).filter((v) => Number.isFinite(v) && v > 0);
  if (valid.length < 2) return empty;

  const dailyReturns = [];
  for (let i = 1; i < valid.length; i++) {
    dailyReturns.push(valid[i] / valid[i - 1] - 1);
  }
  if (dailyReturns.length === 0) return empty;

  const totalReturn = valid[valid.length - 1] / valid[0] - 1;
  const years = totalDays / TRADING_DAYS_PER_YEAR;
  const cagr = (1 + totalReturn) ** (1 / years) - 1;

  const meanDaily = mean(dailyReturns);
  const variance = mean(dailyReturns.map((r) => (r - meanDaily) ** 2));
  const stdevDaily = Math.sqrt(variance);
  const volatility = stdevDaily * Math.sqrt(TRADING_DAYS_PER_YEAR);
  const sharpe = stdevDaily > 1e-9
    ? (meanDaily / stdevDaily) * Math.sqrt(TRADING_DAYS_PER_YEAR)
    : null;

  let peak = valid[0];
  let maxDrawdown = 0;
  for (const v of valid) {
    if (v > peak) peak = v;
    const drawdown = v / peak - 1;
    if (drawdown < maxDrawdown) maxDrawdown = drawdown;
  }

  return {
    totalReturn: round(totalReturn * 100, 2),
    cagr: round(cagr * 100, 2),
    volatility: round(volatility * 100, 2),
    sharpe: sharpe == null ? null : round(sharpe, 2),
    maxDrawdown: round(maxDrawdown * 100, 2),
  };
};

// Ulcer Index: RMS of the percentage-drawdown series. `null` on <2 valid
// points, matching the backend convention.
export const computeUlcerIndex = (valuesAscending) => {
  const valid = (valuesAscending || []).filter((v) => Number.isFinite(v) && v > 0);
  if (valid.length < 2) return null;
  let peak = valid[0];
  let sumSquares = 0;
  for (const v of valid) {
    if (v > peak) peak = v;
    const drawdownPct = ((v / peak) - 1) * 100;
    sumSquares += drawdownPct * drawdownPct;
  }
  return Math.sqrt(sumSquares / valid.length);
};

// Martin Ratio = CAGR / Ulcer Index. `null` when either input is missing or
// the ulcer index is non-positive (a flat series never drew down).
export const computeMartinRatio = (cagrPct, ulcerIndex) => {
  if (!Number.isFinite(cagrPct) || !Number.isFinite(ulcerIndex) || ulcerIndex <= 0) return null;
  return round(cagrPct / ulcerIndex, 2);
};

/**
 * Derive the full strategy-vs-SPY stat set over a windowed slice of history.
 *
 * `windowedPoints` is the SAME oldest->newest array of points a chart builds
 * for its selected preset (each point carries `spy`, `score`, and the per-mode
 * strategy field named by `modeField` as a flat object with `equity`,
 * `position`, etc.).
 *
 * Returns an object shaped like the backend's `strategy[mode]` summary
 * (totalReturn/cagr/volatility/sharpe/martinRatio/maxDrawdown/timeInvested and
 * their `spy*` counterparts), so the presentational table can consume window
 * stats and backend stats interchangeably. Fields are `null` when the window
 * is too small to compute them (mirrors the backend's empty-window convention).
 */
export const computeWindowStats = (windowedPoints, modeField, lag = false) => {
  const points = windowedPoints || [];

  const equityValues = points.map((d) => selectLag(d[modeField], lag)?.equity);
  const spyValues = points.map((d) => d.spy);

  const strat = computeSeriesStats(equityValues);
  const spy = computeSeriesStats(spyValues);

  const stratUlcer = computeUlcerIndex(equityValues);
  const spyUlcer = computeUlcerIndex(spyValues);

  // Time invested: % of windowed days the mode held SPY. Counts only days that
  // actually carry a position for this permutation, matching the equity
  // series' validity. SPY buy-and-hold is always 100% by construction.
  const positioned = points
    .map((d) => selectLag(d[modeField], lag)?.position)
    .filter((p) => typeof p === 'string');
  const investedDays = positioned.filter((p) => p === 'invested').length;
  const timeInvested = positioned.length > 0
    ? round((investedDays / positioned.length) * 100, 1)
    : null;

  return {
    totalReturn: strat.totalReturn,
    spyTotalReturn: spy.totalReturn,
    cagr: strat.cagr,
    spyCagr: spy.cagr,
    volatility: strat.volatility,
    spyVolatility: spy.volatility,
    sharpe: strat.sharpe,
    spySharpe: spy.sharpe,
    martinRatio: computeMartinRatio(strat.cagr, stratUlcer),
    spyMartinRatio: computeMartinRatio(spy.cagr, spyUlcer),
    maxDrawdown: strat.maxDrawdown,
    spyMaxDrawdown: spy.maxDrawdown,
    timeInvested,
    spyTimeInvested: timeInvested == null ? null : 100,
  };
};

// ─── Row builders ───────────────────────────────────────────────────────────
//
// Each card asks for a ROW SET tuned to its purpose (Q1b). A row is
// { label, strategy, spy } already run through the right formatter, so the
// presentational table stays dumb. `stats` is either a backend `strategy[mode]`
// object or a computeWindowStats() result — same shape either way.

// The full comparison used by the Strategy Performance card: return-oriented
// AND risk-oriented rows.
export const buildFullStatRows = (stats) => [
  { label: 'Total Return', strategy: formatPct(stats.totalReturn), spy: formatPct(stats.spyTotalReturn) },
  { label: 'CAGR', strategy: formatPct(stats.cagr), spy: formatPct(stats.spyCagr) },
  // Volatility is a dispersion measure (always ≥ 0), so it uses the unsigned
  // formatter — a leading '+' would falsely imply direction, the same reasoning
  // that applies to Time Invested below.
  { label: 'Volatility', strategy: formatUnsignedPct(stats.volatility), spy: formatUnsignedPct(stats.spyVolatility) },
  { label: 'Sharpe', strategy: formatRatio(stats.sharpe), spy: formatRatio(stats.spySharpe) },
  { label: 'Martin Ratio', strategy: formatRatio(stats.martinRatio), spy: formatRatio(stats.spyMartinRatio) },
  { label: 'Max Drawdown', strategy: formatPct(stats.maxDrawdown), spy: formatPct(stats.spyMaxDrawdown) },
  { label: 'Time Invested', strategy: formatUnsignedPct(stats.timeInvested), spy: formatUnsignedPct(stats.spyTimeInvested) },
];

// Return-focused subset for the S&P 500 — Market Health Strategy card: the
// chart already shows the equity curves, so the table leads with the headline
// return/growth figures plus the two risk framings, and drops Volatility/Time
// Invested to stay compact under an already stat-rich chart.
export const buildReturnStatRows = (stats) => [
  { label: 'Total Return', strategy: formatPct(stats.totalReturn), spy: formatPct(stats.spyTotalReturn) },
  { label: 'CAGR', strategy: formatPct(stats.cagr), spy: formatPct(stats.spyCagr) },
  { label: 'Sharpe', strategy: formatRatio(stats.sharpe), spy: formatRatio(stats.spySharpe) },
  { label: 'Max Drawdown', strategy: formatPct(stats.maxDrawdown), spy: formatPct(stats.spyMaxDrawdown) },
];

// Drawdown-focused subset for the Drawdown from Peak card: the card is about
// downside, so it leads with Max Drawdown and the drawdown-weighted Martin
// Ratio, adds a Δ vs SPY row (drawdown gap in percentage points — the card's
// own framing), and Time Invested (how much of the window it stayed exposed).
// Deliberately omits Total Return / CAGR / Sharpe, which belong to the
// return-oriented cards.
export const buildDrawdownStatRows = (stats) => {
  const delta = Number.isFinite(stats.maxDrawdown) && Number.isFinite(stats.spyMaxDrawdown)
    ? stats.maxDrawdown - stats.spyMaxDrawdown
    : NaN;
  return [
    { label: 'Max Drawdown', strategy: formatPct(stats.maxDrawdown), spy: formatPct(stats.spyMaxDrawdown) },
    { label: 'Δ vs SPY', strategy: formatDeltaPp(delta), spy: '—' },
    { label: 'Martin Ratio', strategy: formatRatio(stats.martinRatio), spy: formatRatio(stats.spyMartinRatio) },
    { label: 'Time Invested', strategy: formatUnsignedPct(stats.timeInvested), spy: formatUnsignedPct(stats.spyTimeInvested) },
  ];
};
